import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmtDateTimeIST } from "@/lib/format";
import { NoteComposer } from "@/components/NoteComposer";
import { CallHistory } from "@/components/LogCall";

import {
  Loader2, Phone, MessageCircle, GitBranch, FileText, Users, Megaphone,
  StickyNote, Settings, History as HistoryIcon, Calendar,
} from "lucide-react";

const CHIPS = [
  ["all", "All"], ["note", "Notes"], ["call", "Calls"], ["whatsapp", "WhatsApp"],
  ["stage", "Stage"], ["documents", "Documents"], ["partner", "Partner"],
  ["standup", "Standup"], ["consultation", "Consultations"], ["system", "System"], ["field", "Fields"],
];
// Item 6: call-type notes belong under Calls; meeting notes under Notes.
const matchesFilter = (e, filter) => filter === "all" || e.kind === filter
  || (filter === "note" && e.kind === "meeting") || (filter === "call" && e.kind === "meeting" && /^\[call\]/i.test(e.text || ""));

const ICON = {
  note: StickyNote, call: Phone, whatsapp: MessageCircle, stage: GitBranch,
  documents: FileText, partner: Megaphone, standup: Users, system: Settings,
  field: HistoryIcon, consultation: Calendar, meeting: Users,
};
const COLOR = {
  note: "#6B7280", call: "#38A2FF", whatsapp: "#159A50", stage: "#FF4802",
  documents: "#F5B71D", partner: "#EA45C6", standup: "#8B46FF", system: "#6B7280",
  field: "#6B7280", consultation: "#38A2FF",
};

const URL_SPLIT_RE = /(https?:\/\/[^\s<>)\]]+)/g;

function Linkify({ text }) {
  const s = String(text ?? "");
  const parts = s.split(URL_SPLIT_RE);
  return (
    <>
      {parts.map((p, i) =>
        i % 2 === 1 ? (
          <a key={i} href={p.replace(/[.,;]+$/, "")} target="_blank" rel="noreferrer"
            className="text-[#38A2FF] hover:underline break-all">{p.replace(/[.,;]+$/, "")}</a>
        ) : (
          <React.Fragment key={i}>{p}</React.Fragment>
        )
      )}
    </>
  );
}

export function TimelineTab({ recordId }) {
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { setData((await api.get(`/cases/${recordId}/timeline`)).data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [recordId]);

  const events = (data?.events || []).filter((e) => matchesFilter(e, filter));

  return (
    <div className="grid grid-cols-12 gap-6">
      <div className="col-span-12 lg:col-span-8 space-y-4">
        <NoteComposer recordId={recordId} onDone={load} />
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-4 shadow-sm" data-testid="timeline-calls"><div className="text-xs font-semibold text-[#333333] mb-2">Pre-Sales calls</div><CallHistory recordId={recordId} /></div>
        <div className="rounded-2xl border border-[#E2E2E2] bg-white p-5 shadow-sm" data-testid="timeline-feed">
          <div className="flex flex-wrap gap-1.5 mb-4">
            {CHIPS.map(([k, l]) => (
              <button key={k} onClick={() => setFilter(k)} data-testid={`timeline-chip-${k}`}
                className={`px-2.5 py-0.5 rounded-full text-[11px] border transition-colors ${filter === k ? "bg-[#FF4802] text-white border-[#FF4802]" : "bg-white text-[#6B7280] border-[#E2E2E2] hover:border-[#FF6F38]"}`}>{l}</button>
            ))}
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-[#6B7280] text-sm py-8 justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#FF4802]" /> Loading timeline…</div>
          ) : events.length === 0 ? (
            <p className="text-sm text-[#6B7280] py-6 text-center">No events for this filter.</p>
          ) : (
            <div className="space-y-3">
              {events.map((e, i) => {
                const Ic = ICON[e.kind] || StickyNote;
                return (
                  <div key={i} className="flex gap-3" data-testid={`timeline-event-${e.kind}`}>
                    <div className="flex flex-col items-center">
                      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${COLOR[e.kind] || "#6B7280"}1a` }}>
                        <Ic className="w-3.5 h-3.5" style={{ color: COLOR[e.kind] || "#6B7280" }} />
                      </div>
                      {i < events.length - 1 && <div className="w-px flex-1 bg-[#E2E2E2] my-1" />}
                    </div>
                    <div className="pb-1 min-w-0">
                      {e.label && <div className="text-[11px] uppercase text-[#6B7280] font-medium">{e.label}</div>}
                      <div className="text-sm text-[#333333] whitespace-pre-wrap break-words"><Linkify text={e.text} /></div>
                      {(e.notes_link || e.recording_link) && (
                        <div className="flex gap-2 mt-1">
                          {e.notes_link && <a href={e.notes_link} target="_blank" rel="noreferrer" data-testid="timeline-notes-link" className="text-xs text-[#38A2FF] border border-[#38A2FF]/30 rounded-lg px-2.5 py-1 hover:bg-[#38A2FF]/5">Notes</a>}
                          {e.recording_link && <a href={e.recording_link} target="_blank" rel="noreferrer" data-testid="timeline-recording-link" className="text-xs text-[#8B46FF] border border-[#8B46FF]/30 rounded-lg px-2.5 py-1 hover:bg-[#8B46FF]/5">Recording</a>}
                        </div>
                      )}
                      <div className="text-[11px] text-[#C5C4C4] mt-0.5">
                        {e.actor ? `${e.actor} · ` : ""}{e.when ? (String(e.when).includes("T") ? fmtDateTimeIST(e.when) : e.when) : ""}
                        {e.recording && <a href={e.recording} target="_blank" rel="noreferrer" className="text-[#38A2FF] ml-2 hover:underline">recording</a>}
                        {e.link && <a href={e.link} target="_blank" rel="noreferrer" data-testid="timeline-event-link" className="text-[#38A2FF] ml-2 hover:underline">{e.link_label || "Open link"}</a>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
